/**
 * useApplyThemePreference — applies a resolved theme preference to the
 * `<html>` element's `data-theme` attribute.
 *
 *   - `light` / `dark` → `document.documentElement[data-theme]` is set, which
 *     `index.css` pins via `:root[data-theme="…"] { color-scheme: … }`.
 *   - `system` → the attribute is removed, so `:root { color-scheme: light dark }`
 *     + every `light-dark(…)` token follows the user agent / OS preference.
 *
 * Mirrors the PMS root's applier (`widgets/dashboard/DashboardPage.tsx`
 * `applyTheme`), extracted so the admin surface honors the same preference.
 *
 * **Param-driven by design.** The caller (`AdminTopPill`) owns the
 * `useUserPreferences()` read and passes the resolved theme in. That keeps this
 * a pure DOM-effect hook — no query-client / localStorage coupling — so it (and
 * its consumers' tests) stay trivial.
 *
 * Density is intentionally NOT handled here: unlike theme, density is consumed
 * directly by components (e.g. `CompaniesListPage` reads `preferences.density`),
 * not via a DOM attribute. Inventing a `data-density` attribute nothing reads
 * would be dead mechanism.
 *
 * Follow-up: `DashboardPage`'s inline `applyTheme` should converge onto this
 * hook so the PMS root and the admin surface share one applier.
 */
import { useEffect } from 'react';
import type { ThemePreference } from './useUserPreferences.js';

export function useApplyThemePreference(theme: ThemePreference): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
      return;
    }
    root.setAttribute('data-theme', theme);
  }, [theme]);
}
